import type { SecuritySymbol } from '../../domain/symbol.domain';
import type { AutoSuggestSymbol } from '../../domain/symbol-lookup.domain';

export interface SymbolLookupModelResolverOptions<T extends AutoSuggestSymbol> {
  readonly currentValue: T | null | undefined;
  readonly nextSymbol: SecuritySymbol | AutoSuggestSymbol | null | undefined;
  readonly overwrite: boolean;
}

/**
 * Resolves the next SymbolLookup model value while preserving the existing
 * setModel() update semantics.
 *
 * Empty is always normalized to `null`, never `undefined`: emitting `undefined` to a
 * Signal Forms owner deletes the backing property and orphans the field (NG01902).
 * See hazard H3 in `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
 */
export function resolveSymbolLookupModelValue<T extends AutoSuggestSymbol>(
  options: SymbolLookupModelResolverOptions<T>
): T | null {
  const {
    currentValue,
    nextSymbol,
    overwrite
  }: SymbolLookupModelResolverOptions<T> = options;

  if (overwrite) {
    return (nextSymbol ?? null) as T | null;
  }

  if (currentValue === undefined || currentValue === null) {
    return (nextSymbol ?? null) as T | null;
  }

  if (
    nextSymbol !== undefined
    && nextSymbol !== null
    && currentValue.symbol !== undefined
    && (nextSymbol.symbol !== currentValue.symbol || nextSymbol.marketType !== currentValue.marketType)
  ) {
    return nextSymbol as T;
  }

  return currentValue;
}

